import React from 'react';
import { Text, Group } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { IconCloudUpload, IconPlus, IconPaperclip } from '@tabler/icons-react';
import type { DropZoneProps } from './FileUpload.types';
import styles from './FileUpload.module.css';

export const DropZone = React.memo<DropZoneProps>(
  ({
    disabled = false,
    dragOver,
    hasFiles,
    mode,
    onClick,
    onKeyDown,
    onDragEnter,
    onDragLeave,
    onDragOver,
    onDrop,
    description,
    formats,
  }) => {
    const { t } = useTranslation();

    const zoneClassName = `${hasFiles ? styles.compactDropZone : styles.uploadZone} ${dragOver ? styles.dragOver : ''} ${disabled ? styles.disabled : ''}`;

    return (
      <div
        className={zoneClassName}
        onDragEnter={onDragEnter}
        onDragLeave={onDragLeave}
        onDragOver={onDragOver}
        onDrop={onDrop}
        onClick={onClick}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label={t('fileUpload.uploadArea')}
        onKeyDown={onKeyDown}
      >
        {hasFiles ? (
          <Group gap="sm" className={styles.compactContent}>
            <IconPlus className={styles.compactIcon} />
            <Text size="sm" fw={500}>
              {mode === 'single'
                ? t('fileUpload.replaceFile', { defaultValue: 'Replace file' })
                : t('fileUpload.addMoreFiles')}{' '}
              <Text component="span" size="xs" c="dimmed">
                {mode === 'single'
                  ? t('fileUpload.orDragIt')
                  : t('fileUpload.orDragThem')}
              </Text>
            </Text>
          </Group>
        ) : (
          <div className={styles.uploadContent}>
            <IconCloudUpload className={styles.uploadIcon} />

            <Text className={styles.uploadTitle}>
              {t('fileUpload.dragDropFiles')}
            </Text>

            <Text className={styles.uploadDescription}>
              {description ?? t('fileUpload.maxSize')}
            </Text>

            <div className={styles.uploadFormats}>
              <IconPaperclip size={12} />
              {formats ?? t('fileUpload.supportedFormats')}
            </div>
          </div>
        )}
      </div>
    );
  }
);

DropZone.displayName = 'DropZone';
